import { ReadonlyVec3 } from 'gl-matrix';
import type { SliceAxis } from '~/engine/data/common/Grid';
import { ACQUIRED_SLICE_AXIS } from '~/engine/view/planeViewGeometry';
import { Orientations } from '~/engine/view/views/PlaneView';
import { PATIENT_AXES } from '~/utils/obliquity';

/** The parts of a PlaneView {@link isViewDrawable} reads. */
export interface DrawableViewLike {
    imageSliceAxis: SliceAxis | undefined;
}

/**
 * Whether this view shows the acquired slices of its image: its plane is
 * normal to the image's slice axis ({@link ACQUIRED_SLICE_AXIS}), whatever
 * the patient orientation of the acquisition.
 *
 * A view that cuts across the acquired slices (a sagittal or coronal view
 * of a transversal acquisition, or a view turned away from the slices)
 * shows no acquired slice, and is refused rather than written as though it
 * showed one.
 *
 * Which image of a fused pair the view draws plays no part, so a stroke can
 * be drawn on a view that shows the registered secondary image alone. An
 * action that changes a whole slice asks the stricter
 * `viewShowsAcquiredPrimarySlice`, which also requires the primary image
 * on screen.
 */
export function isViewDrawable(view: DrawableViewLike): boolean {
    return view.imageSliceAxis === ACQUIRED_SLICE_AXIS;
}

/** The parts of a PlaneView {@link rectangleSelectAxis} reads to name the
 *  patient axis a view slices along. */
export interface LabelledViewLike {
    label?: Orientations;
    isPatientAligned(): boolean;
}

/**
 * The extra term {@link rectangleSelectAxis} needs beyond
 * {@link LabelledViewLike}: the plane normal it measures against the grid.
 * Unlike the drawable gate it also reads `label` and `isPatientAligned()`:
 * a rectangle is drawn in the view's own plane, so a frame turned about its
 * normal, which leaves `viewDirection` untouched, still tilts the rectangle
 * against the patient axes.
 */
export interface RectangleSelectViewLike extends LabelledViewLike {
    viewDirection: ReadonlyVec3;
}

/** The one thing the rectangle-select gate asks of a grid. Structural so
 *  the gate can be exercised without a WebGL-backed {@link Grid}. */
export interface AxisAlignedGridLike {
    axisAlong(patientDir: ReadonlyVec3): SliceAxis | undefined;
}

/** Whether a cross-section may be drawn: any patient-aligned view, on any plane. */
export function isViewCrossSectionDrawable(view: LabelledViewLike): boolean {
    return view.label !== undefined && view.isPatientAligned();
}

/**
 * Whether a rectangle selection can be resolved against this grid at all —
 * the grid-only half of {@link rectangleSelectAxis}, for the UI gates that
 * must decide before any view or drag exists.
 *
 * A rectangle drag is reduced to an axis-aligned WORLD box
 * (`WorldBoundsMm`), so every patient axis has to name a grid axis for that
 * box to describe voxels. When one does not — an oblique or gantry-tilted
 * acquisition — the box becomes the AABB of the drawn rectangle: larger
 * than the rectangle by roughly `sin(theta) x drag size` (~8.7 mm for a
 * 10 deg tilt over a 50 mm drag), an order of magnitude past any voxel
 * spacing, and always in the OVER-delete direction. Contour the user never
 * enclosed would be erased with nothing on screen to reveal it, so the
 * gesture is refused rather than approximated.
 *
 * True for the 90-degree coronal and sagittal acquisitions this tool must
 * keep supporting: those permute the patient axes among the grid axes, and
 * a permutation still has an axis along each patient direction.
 */
export function isGridRectangleSelectable(grid: AxisAlignedGridLike): boolean {
    return PATIENT_AXES.every((dir) => grid.axisAlong(dir) !== undefined);
}

/**
 * The PATIENT axis this view slices along, or undefined when it slices
 * along none. The label alone cannot decide it — it keeps its name however
 * the view matrix is turned — so alignment is measured first and the label
 * then only names which of the three aligned axes it is.
 *
 * A patient axis, not a grid axis: the caller indexes patient-space bounds
 * with it, and on an obliquely acquired series the two do not coincide.
 */
function patientSliceAxis(view: LabelledViewLike): SliceAxis | undefined {
    if (!view.isPatientAligned()) {
        return undefined;
    }
    switch (view.label) {
        case Orientations.TRANSVERSAL_ORIENTATION:
            return 2;
        case Orientations.CORONAL_ORIENTATION:
            return 1;
        case Orientations.SAGITTAL_ORIENTATION:
            return 0;
        default:
            return undefined;
    }
}

/**
 * The WORLD axis a rectangle dragged on `view` is extruded along, or
 * `undefined` when the drag must be refused.
 *
 * The single geometric verdict behind rectangle-select: the press gate, the
 * extrusion inside {@link RectangleSelectTool}, the arm path in
 * `buildToolConfig` and the eraser's rectangle shape all resolve to this
 * function (or to its grid-only factor
 * {@link isGridRectangleSelectable}), so they cannot reach opposite
 * conclusions about the same series.
 *
 * Three terms, each refusing a distinct hazard:
 *  - {@link patientSliceAxis}: names the patient axis from the orientation
 *    label and, in the same step, rejects a user-rotated frame — `label`
 *    alone keeps its name however the view matrix is turned.
 *  - {@link isGridRectangleSelectable}: the grid must lie along the patient
 *    axes for the world box to describe voxels.
 *  - the closing equality: the axis the box is extruded on must be the axis
 *    the view actually looks down. `isPatientAligned()` measures the view
 *    frame against a base derived from the image grid, so on an obliquely
 *    acquired series it reports aligned while the plane normal is not a
 *    patient axis; asking the grid about both directions is what catches
 *    that.
 *
 * Rectangle-select is armed on all three orthogonal
 * views and takes its extruded axis from whichever one the press landed on.
 * It also asks a different grid: the drawable gate asks the image the view
 * shows, this one asks the ROI the drag will clear.
 *
 * @param view The view the press landed on
 * @param grid The grid the selection will be resolved against — the target
 *     ROI's own texture grid
 */
export function rectangleSelectAxis(
    view: RectangleSelectViewLike,
    grid: AxisAlignedGridLike
): SliceAxis | undefined {
    const worldAxis = patientSliceAxis(view);
    if (worldAxis === undefined || !isGridRectangleSelectable(grid)) {
        return undefined;
    }
    if (
        grid.axisAlong(view.viewDirection) !==
        grid.axisAlong(PATIENT_AXES[worldAxis])
    ) {
        return undefined;
    }
    return worldAxis;
}
